import { SelectOption } from '@/models/selectOption'

export const USER_STATUS = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
}

export const USER_STATUS_LABELS = {
  [USER_STATUS.ACTIVE]: 'Ativo',
  [USER_STATUS.INACTIVE]: 'Inativo',
}

export const USER_STATUS_COLORS = {
  [USER_STATUS.ACTIVE]: '#008900',
  [USER_STATUS.INACTIVE]: '#898989',
}

export const USER_STATUS_OPTIONS: SelectOption[] = [
  {
    label: USER_STATUS_LABELS[USER_STATUS.ACTIVE],
    value: USER_STATUS.ACTIVE,
  },
  {
    label: USER_STATUS_LABELS[USER_STATUS.INACTIVE],
    value: USER_STATUS.INACTIVE,
  },
]

export const CONVERSATION_NOTIFICATION_PREFERENCE = {
  ALL: 'all',
  ASSIGNED: 'assigned',
  NONE: 'none',
} as const

export type ConversationNotificationPreference =
  (typeof CONVERSATION_NOTIFICATION_PREFERENCE)[keyof typeof CONVERSATION_NOTIFICATION_PREFERENCE]

export const CONVERSATION_NOTIFICATION_PREFERENCE_OPTIONS: SelectOption[] = [
  {
    label: 'Todos os atendimentos',
    value: CONVERSATION_NOTIFICATION_PREFERENCE.ALL,
  },
  {
    label: 'Apenas atendimentos atribuídos a mim',
    value: CONVERSATION_NOTIFICATION_PREFERENCE.ASSIGNED,
  },
  {
    label: 'Nenhuma notificação',
    value: CONVERSATION_NOTIFICATION_PREFERENCE.NONE,
  },
]

export const COMPANY_STATUS = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  PENDING_PAYMENT: 'pending_payment',
  PAYMENT_FAILED: 'payment_failed',
  PAYMENT_ABANDONED: 'payment_abandoned',
  RESTRICTED_ACCESS: 'restricted_access',
  PLAN_LIMIT_EXCEEDED: 'plan_limit_exceeded',
  CANCELED: 'canceled',
}

export const COMPANY_STATUS_LABELS = {
  [COMPANY_STATUS.ACTIVE]: 'Ativo',
  [COMPANY_STATUS.INACTIVE]: 'Inativo',
  [COMPANY_STATUS.PENDING_PAYMENT]: 'Pagamento pendente',
  [COMPANY_STATUS.PAYMENT_FAILED]: 'Pagamento falhou',
  [COMPANY_STATUS.PAYMENT_ABANDONED]: 'Abandonou checkout',
  [COMPANY_STATUS.RESTRICTED_ACCESS]: 'Acesso restrito',
  [COMPANY_STATUS.PLAN_LIMIT_EXCEEDED]: 'Limite do plano excedido',
  [COMPANY_STATUS.CANCELED]: 'Assinatura cancelada',
}

export const COMPANY_STATUS_OPTIONS: SelectOption[] = [
  {
    label: COMPANY_STATUS_LABELS[COMPANY_STATUS.ACTIVE],
    value: COMPANY_STATUS.ACTIVE,
  },
  {
    label: COMPANY_STATUS_LABELS[COMPANY_STATUS.INACTIVE],
    value: COMPANY_STATUS.INACTIVE,
  },
  {
    label: COMPANY_STATUS_LABELS[COMPANY_STATUS.PENDING_PAYMENT],
    value: COMPANY_STATUS.PENDING_PAYMENT,
  },
  {
    label: COMPANY_STATUS_LABELS[COMPANY_STATUS.PAYMENT_FAILED],
    value: COMPANY_STATUS.PAYMENT_FAILED,
  },
  {
    label: COMPANY_STATUS_LABELS[COMPANY_STATUS.PAYMENT_ABANDONED],
    value: COMPANY_STATUS.PAYMENT_ABANDONED,
  },
  {
    label: COMPANY_STATUS_LABELS[COMPANY_STATUS.RESTRICTED_ACCESS],
    value: COMPANY_STATUS.RESTRICTED_ACCESS,
  },
  {
    label: COMPANY_STATUS_LABELS[COMPANY_STATUS.PLAN_LIMIT_EXCEEDED],
    value: COMPANY_STATUS.PLAN_LIMIT_EXCEEDED,
  },
  {
    label: COMPANY_STATUS_LABELS[COMPANY_STATUS.CANCELED],
    value: COMPANY_STATUS.CANCELED,
  },
]

export const CLIENT_STATUS = {
  ACTIVE: 'active',
  SERVICE_SUSPENDED: 'service_suspended',
  INACTIVE: 'inactive',
  DEFAULTED: 'defaulted',
  NOT_CLIENT: 'not_client',
  NOT_IDENTIFIED: 'not_identified',
  COMERCIAL_PARTNER: 'comercial_partner',
  BUSINESS_PARTNER: 'business_partner',
  INTERSTATE_PARTNER: 'interstate_partner',
  PUBLIC_AGENCY: 'public_agency',
  LABOR_UNION: 'labor_union',
  NEGOTIATING_CLIENT: 'negotiating_client',
  FUTURE_CLIENT: 'future_client',
  BANK: 'bank',
  SUPPLIER: 'supplier',
  NON_RECURRING: 'non_recurring',
  RECURRING_ANNUAL: 'recurring_annual',
}

/** Status de cliente que não recebem execução de etapa da jornada. */
export const CLIENT_STATUSES_WITHOUT_STAGE_EXECUTION = [
  CLIENT_STATUS.INACTIVE,
  CLIENT_STATUS.NOT_CLIENT,
  CLIENT_STATUS.NOT_IDENTIFIED,
]

export const CLIENT_STATUS_LABELS = {
  [CLIENT_STATUS.ACTIVE]: 'Ativo',
  [CLIENT_STATUS.SERVICE_SUSPENDED]: 'Serviço suspenso',
  [CLIENT_STATUS.INACTIVE]: 'Inativo',
  [CLIENT_STATUS.DEFAULTED]: 'Inadimplente',
  [CLIENT_STATUS.NOT_CLIENT]: 'Não Cliente',
  [CLIENT_STATUS.NOT_IDENTIFIED]: 'Não Identificado',
  [CLIENT_STATUS.COMERCIAL_PARTNER]: 'Parceiro Comercial',
  [CLIENT_STATUS.BUSINESS_PARTNER]: 'Parceiro de Negócios',
  [CLIENT_STATUS.INTERSTATE_PARTNER]: 'Parceiro Interestadual',
  [CLIENT_STATUS.PUBLIC_AGENCY]: 'Órgão Público',
  [CLIENT_STATUS.LABOR_UNION]: 'Sindicato',
  [CLIENT_STATUS.NEGOTIATING_CLIENT]: 'Cliente em negociação',
  [CLIENT_STATUS.FUTURE_CLIENT]: 'Futuro Cliente',
  [CLIENT_STATUS.BANK]: 'Banco',
  [CLIENT_STATUS.SUPPLIER]: 'Fornecedor',
  [CLIENT_STATUS.NON_RECURRING]: 'Não Recorrente',
  [CLIENT_STATUS.RECURRING_ANNUAL]: 'Recorrente Anual',
}

export const CLIENT_STATUS_COLORS = {
  [CLIENT_STATUS.ACTIVE]: '#1a0f33',
  [CLIENT_STATUS.SERVICE_SUSPENDED]: '#827599',
  [CLIENT_STATUS.INACTIVE]: '#ada8b8',

  [CLIENT_STATUS.DEFAULTED]: '#2a174a',
  [CLIENT_STATUS.NOT_CLIENT]: '#3a2161',
  [CLIENT_STATUS.NOT_IDENTIFIED]: '#4b2f7a',

  [CLIENT_STATUS.COMERCIAL_PARTNER]: '#654fa1', //cor base
  [CLIENT_STATUS.BUSINESS_PARTNER]: '#7b63b3',
  [CLIENT_STATUS.INTERSTATE_PARTNER]: '#9077c4',
  [CLIENT_STATUS.PUBLIC_AGENCY]: '#a78ed4',
  [CLIENT_STATUS.LABOR_UNION]: '#bda6e0',
  [CLIENT_STATUS.NEGOTIATING_CLIENT]: '#c8b3e8',
  [CLIENT_STATUS.FUTURE_CLIENT]: '#d1c0ec',
  [CLIENT_STATUS.BANK]: '#dacdf0',
  [CLIENT_STATUS.SUPPLIER]: '#e3daf4',
  [CLIENT_STATUS.NON_RECURRING]: '#d2c1ea',
  [CLIENT_STATUS.RECURRING_ANNUAL]: '#ddd4f1',
}

export const CLIENT_STATUS_OPTIONS: SelectOption[] = [
  {
    label: CLIENT_STATUS_LABELS[CLIENT_STATUS.ACTIVE],
    value: CLIENT_STATUS.ACTIVE,
  },
  {
    label: CLIENT_STATUS_LABELS[CLIENT_STATUS.INACTIVE],
    value: CLIENT_STATUS.INACTIVE,
  },
  {
    label: CLIENT_STATUS_LABELS[CLIENT_STATUS.NOT_CLIENT],
    value: CLIENT_STATUS.NOT_CLIENT,
  },
  {
    label: CLIENT_STATUS_LABELS[CLIENT_STATUS.COMERCIAL_PARTNER],
    value: CLIENT_STATUS.COMERCIAL_PARTNER,
  },
  {
    label: CLIENT_STATUS_LABELS[CLIENT_STATUS.BUSINESS_PARTNER],
    value: CLIENT_STATUS.BUSINESS_PARTNER,
  },
  {
    label: CLIENT_STATUS_LABELS[CLIENT_STATUS.INTERSTATE_PARTNER],
    value: CLIENT_STATUS.INTERSTATE_PARTNER,
  },
  {
    label: CLIENT_STATUS_LABELS[CLIENT_STATUS.PUBLIC_AGENCY],
    value: CLIENT_STATUS.PUBLIC_AGENCY,
  },
  {
    label: CLIENT_STATUS_LABELS[CLIENT_STATUS.LABOR_UNION],
    value: CLIENT_STATUS.LABOR_UNION,
  },
  {
    label: CLIENT_STATUS_LABELS[CLIENT_STATUS.NEGOTIATING_CLIENT],
    value: CLIENT_STATUS.NEGOTIATING_CLIENT,
  },
  {
    label: CLIENT_STATUS_LABELS[CLIENT_STATUS.FUTURE_CLIENT],
    value: CLIENT_STATUS.FUTURE_CLIENT,
  },
  {
    label: CLIENT_STATUS_LABELS[CLIENT_STATUS.BANK],
    value: CLIENT_STATUS.BANK,
  },
  {
    label: CLIENT_STATUS_LABELS[CLIENT_STATUS.SUPPLIER],
    value: CLIENT_STATUS.SUPPLIER,
  },
  {
    label: CLIENT_STATUS_LABELS[CLIENT_STATUS.NON_RECURRING],
    value: CLIENT_STATUS.NON_RECURRING,
  },
  {
    label: CLIENT_STATUS_LABELS[CLIENT_STATUS.RECURRING_ANNUAL],
    value: CLIENT_STATUS.RECURRING_ANNUAL,
  },
  {
    label: CLIENT_STATUS_LABELS[CLIENT_STATUS.DEFAULTED],
    value: CLIENT_STATUS.DEFAULTED,
  },
  {
    label: CLIENT_STATUS_LABELS[CLIENT_STATUS.SERVICE_SUSPENDED],
    value: CLIENT_STATUS.SERVICE_SUSPENDED,
  },
].sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'))

export const CLIENT_STATUS_FILTER_OPTIONS: SelectOption[] = [
  ...CLIENT_STATUS_OPTIONS,
  {
    label: CLIENT_STATUS_LABELS[CLIENT_STATUS.NOT_IDENTIFIED],
    value: CLIENT_STATUS.NOT_IDENTIFIED,
  },
].sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'))

export const REFERRAL_COMMISSION_STATUS = {
  PENDING: 'pending',
  PAID: 'paid',
} as const

export type ReferralCommissionStatus =
  (typeof REFERRAL_COMMISSION_STATUS)[keyof typeof REFERRAL_COMMISSION_STATUS]

export const REFERRAL_COMMISSION_STATUS_LABELS = {
  [REFERRAL_COMMISSION_STATUS.PENDING]: 'Pendente',
  [REFERRAL_COMMISSION_STATUS.PAID]: 'Paga',
} as const

export const REFERRAL_COMMISSION_STATUS_FILTER = {
  ALL: 'all',
  ...REFERRAL_COMMISSION_STATUS,
} as const

export type ReferralCommissionStatusFilter =
  (typeof REFERRAL_COMMISSION_STATUS_FILTER)[keyof typeof REFERRAL_COMMISSION_STATUS_FILTER]

// Registros que ainda não representam um cliente identificado e ativo:
// ficam fora das buscas de cliente e das seleções de jornada.
export const INELIGIBLE_CLIENT_STATUSES = [
  CLIENT_STATUS.INACTIVE,
  CLIENT_STATUS.NOT_CLIENT,
  CLIENT_STATUS.NOT_IDENTIFIED,
]

export const INELIGIBLE_CLIENT_JOURNEY_NOTICE =
  'Este cliente não participa de jornadas enquanto o status for inativo, não cliente ou não identificado.'

export const CONVERSATION_STATUS = {
  WAITING: 'Aguardando atendimento',
  IN_SERVICE: 'Em atendimento',
  FINISHED: 'Finalizada',
  PENDING_FINISH: 'Pendente de finalização',
}

export const CONVERSATION_ROUTING_AREA = {
  ATTENDANCE: 'attendance',
  OPPORTUNITIES: 'opportunities',
} as const

export type ConversationRoutingArea =
  (typeof CONVERSATION_ROUTING_AREA)[keyof typeof CONVERSATION_ROUTING_AREA]

export const MESSAGE_STATUS = {
  SENT: 'SENT',
  RECEIVED: 'RECEIVED',
  READ: 'READ',
  FAILED: 'FAILED',
  PENDING: 'PENDING',
}

export const MEDIA_ENRICHMENT_STATUS = {
  QUEUED: 'queued',
  PROCESSING: 'processing',
  PENDING: 'pending',
  COMPLETED: 'completed',
  FAILED: 'failed',
} as const

export const DEMAND_STATUS = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
}

export const DEMAND_STATUS_LABELS = {
  [DEMAND_STATUS.ACTIVE]: 'Ativa',
  [DEMAND_STATUS.INACTIVE]: 'Inativa',
}

export const DEMAND_STATUS_OPTIONS: SelectOption[] = [
  {
    value: DEMAND_STATUS.ACTIVE,
    label: DEMAND_STATUS_LABELS[DEMAND_STATUS.ACTIVE],
  },
  {
    value: DEMAND_STATUS.INACTIVE,
    label: DEMAND_STATUS_LABELS[DEMAND_STATUS.INACTIVE],
  },
]

export const SERVICE_ORDER_STATUS = {
  OPENED: 'Aberta',
  IN_SERVICE: 'Iniciada',
  WAITING_APPROVAL: 'Aguardando aprovação',
  FINISHED: 'Finalizada',
  REPROVED: 'Reprovada',
  REOPENED: 'Reaberta',
  LATE: 'Atrasada',
  PENDING: 'Pendente',
}

export const SERVICE_ORDER_STATUS_OPTIONS: SelectOption[] = [
  { value: SERVICE_ORDER_STATUS.OPENED, label: SERVICE_ORDER_STATUS.OPENED },
  {
    value: SERVICE_ORDER_STATUS.IN_SERVICE,
    label: SERVICE_ORDER_STATUS.IN_SERVICE,
  },
  {
    value: SERVICE_ORDER_STATUS.WAITING_APPROVAL,
    label: SERVICE_ORDER_STATUS.WAITING_APPROVAL,
  },
  {
    value: SERVICE_ORDER_STATUS.FINISHED,
    label: SERVICE_ORDER_STATUS.FINISHED,
  },
  {
    value: SERVICE_ORDER_STATUS.REPROVED,
    label: SERVICE_ORDER_STATUS.REPROVED,
  },
  {
    value: SERVICE_ORDER_STATUS.REOPENED,
    label: SERVICE_ORDER_STATUS.REOPENED,
  },
]

export const SCHEDULED_MESSAGE_STATUS = {
  PENDING: 'pending',
  WAITING_SLOT: 'waiting_slot',
  PROCESSING: 'processing',
  SENT: 'sent',
  FAILED: 'failed',
  CANCELED: 'canceled',
} as const

export const TASK_STATUS = {
  PENDING: 'pending',
  COMPLETED: 'completed',
} as const

export const TASK_STATUS_LABELS = {
  [TASK_STATUS.PENDING]: 'Pendente',
  [TASK_STATUS.COMPLETED]: 'Concluída',
} as const

export const TASK_STATUS_OPTIONS: SelectOption[] = [
  {
    value: TASK_STATUS.PENDING,
    label: TASK_STATUS_LABELS[TASK_STATUS.PENDING],
  },
  {
    value: TASK_STATUS.COMPLETED,
    label: TASK_STATUS_LABELS[TASK_STATUS.COMPLETED],
  },
]

export const SCHEDULED_MESSAGE_STATUS_LABELS = {
  [SCHEDULED_MESSAGE_STATUS.PENDING]: 'Pendente',
  [SCHEDULED_MESSAGE_STATUS.WAITING_SLOT]: 'Aguardando vez na fila',
  [SCHEDULED_MESSAGE_STATUS.PROCESSING]: 'Processando',
  [SCHEDULED_MESSAGE_STATUS.SENT]: 'Enviada',
  [SCHEDULED_MESSAGE_STATUS.FAILED]: 'Falha no envio',
  [SCHEDULED_MESSAGE_STATUS.CANCELED]: 'Cancelada',
} as const

export const SCHEDULED_MESSAGE_STATUS_OPTIONS: SelectOption[] = Object.values(
  SCHEDULED_MESSAGE_STATUS
).map((status) => ({
  value: status,
  label: SCHEDULED_MESSAGE_STATUS_LABELS[status],
}))

export const TRANSMISSION_LIST_BATCH_STATUS = {
  PENDING: 'pending',
  WAITING_SLOT: 'waiting_slot',
  IN_PROGRESS: 'in_progress',
  PAUSED: 'paused',
  PAUSED_PROVIDER_BLOCK: 'paused_provider_block',
  CANCEL_REQUESTED: 'cancel_requested',
  COMPLETED: 'completed',
  FAILED: 'failed',
  CANCELED: 'canceled',
}

export const TRANSMISSION_LIST_BATCH_STATUS_LABELS = {
  [TRANSMISSION_LIST_BATCH_STATUS.PENDING]: 'Envio pendente',
  [TRANSMISSION_LIST_BATCH_STATUS.WAITING_SLOT]: 'Aguardando vez na fila',
  [TRANSMISSION_LIST_BATCH_STATUS.IN_PROGRESS]: 'Enviando mensagens',
  [TRANSMISSION_LIST_BATCH_STATUS.COMPLETED]: 'Finalizada com sucesso',
  [TRANSMISSION_LIST_BATCH_STATUS.PAUSED]: 'Envio pausado',
  [TRANSMISSION_LIST_BATCH_STATUS.PAUSED_PROVIDER_BLOCK]:
    'Bloqueado por operação externa',
  [TRANSMISSION_LIST_BATCH_STATUS.CANCEL_REQUESTED]: 'Cancelamento solicitado',
  [TRANSMISSION_LIST_BATCH_STATUS.CANCELED]: 'Envio cancelado',
  [TRANSMISSION_LIST_BATCH_STATUS.FAILED]: 'Finalizada com falha(s)',
}

export const TRANSMISSION_BATCH_ITEM_STATUS = {
  PENDING: 'pending',
  PROCESSING: 'processing',
  SENT_TO_PROVIDER: 'sent_to_provider',
  FAILED: 'failed',
  CANCELED: 'canceled',
  SKIPPED: 'skipped',
} as const

export const TRANSMISSION_BATCH_ITEM_STATUS_LABELS = {
  [TRANSMISSION_BATCH_ITEM_STATUS.PENDING]: 'Aguardando envio',
  [TRANSMISSION_BATCH_ITEM_STATUS.PROCESSING]: 'Em processamento',
  [TRANSMISSION_BATCH_ITEM_STATUS.SENT_TO_PROVIDER]: 'Enviado ao provedor',
  [TRANSMISSION_BATCH_ITEM_STATUS.FAILED]: 'Falhou',
  [TRANSMISSION_BATCH_ITEM_STATUS.CANCELED]: 'Cancelado',
  [TRANSMISSION_BATCH_ITEM_STATUS.SKIPPED]:
    'Não elegível no início do lote',
}
